import { inject, observer } from 'mobx-react';
import React, { Component } from 'react';
import { defineMessages, injectIntl } from 'react-intl';
import {
    Aside,
    AsideItem, ContainerWithAside, CreateContentDefinitionForm, FormActions, Loader
} from '../../../components';
import { NOTIFICATION_DEFAULT_MESSAGES, NOTIFICATION_KEY_CONSTANTS } from '../../../constants/notifications-constants';
import { userPermissionsTable } from '../../../constants/user-permissions-table';
import { withFormInitialization } from '../../../hoc';
import { DataAttributesBuilder } from '../../../services/DataAttributesBuilder';
import { ContentDefinitionErrorTypes } from '../../../services/Error/ContentDefinitionErrorManager';
import {
    createCustomNotification,
    createErrorNotification,
    createInfoNotification,
    createSuccessNotification
} from '../../../services/Notification';
import {
    CreateContentDefinitionProps,
    CreateContentDefinitionState,
    InjectedProps
} from './CreateContentDefinitionModel';

const labels = defineMessages({
    validateDefinitionError: {
        id: 'CreateContentDefinition.validateDefinitionError',
        defaultMessage: 'Your definition contain invalid fields, please check them before saving'
    }
});

@inject('notificationStore', 'contentDefinitionFormStore', 'contentDefinitionSchemaStore')
@observer
class CreateContentDefinition extends Component<CreateContentDefinitionProps, CreateContentDefinitionState> {

    get injected() {
        return this.props as InjectedProps;
    }

    async componentDidMount(): Promise<void> {
        await this.injected.contentDefinitionFormStore.setDefinitionSchema();
    }

    createDataAttributes = () => {
        return new DataAttributesBuilder('contentdefinition')
            .setIdAttribute('addContentDefinition')
            .setAddFormAttribute('contentDefinitionCreate')
            .build();
    }

    onSubmitForm = async (values: { name: string, mnemonicId: string, defaultLanguage: string }) => {
        const { formatMessage } = this.props.intl;
        const {
            notificationStore,
            contentDefinitionSchemaStore: { retrieveDeserializedDefinitionSchema, jsonEditorSchema }
        } = this.injected;
        createInfoNotification(
            NOTIFICATION_KEY_CONSTANTS.CONTENTDEFINITION_CREATE_API_KEY_REQUEST,
            formatMessage,
            NOTIFICATION_DEFAULT_MESSAGES.createAction
        );
        try {
            this.injected.contentDefinitionSchemaStore.resetFieldsInitialIndex();
            await this.injected.contentDefinitionFormStore.save({
                name: values.name, mnemonicId: values.mnemonicId,
                schema: !!jsonEditorSchema ? jsonEditorSchema : retrieveDeserializedDefinitionSchema()
            });
            await notificationStore.closeNotification(
                NOTIFICATION_KEY_CONSTANTS.CONTENTDEFINITION_CREATE_API_KEY_REQUEST
            );
            createSuccessNotification(
                formatMessage,
                NOTIFICATION_DEFAULT_MESSAGES.createAction
            );
        } catch (e: any) {
            this.injected.contentDefinitionSchemaStore.setSchemaErrors(e.retrieveValidationErrors());
            await notificationStore.closeNotification(
                NOTIFICATION_KEY_CONSTANTS.CONTENTDEFINITION_CREATE_API_KEY_REQUEST
            );
            if (e.type === ContentDefinitionErrorTypes.FailedSchemaValidation) {
                createCustomNotification(formatMessage, labels.validateDefinitionError, 'error', 5);
            } else {
                createErrorNotification(formatMessage, NOTIFICATION_DEFAULT_MESSAGES.createAction);
            }
        }
    }

    validateMnemonicId = async (mnemonicId: string): Promise<boolean> => {
        return await this.injected.contentDefinitionFormStore.validateContentDefinitionMnemonicId(mnemonicId);
    }

    render() {
        const { notifications } = this.injected.notificationStore;
        const { jsonSchema, loaders: { setDefinitionSchema } } = this.injected.contentDefinitionFormStore;
        if (setDefinitionSchema) {
            return <Loader />;
        }
        return (
            <ContainerWithAside
                disableContent={
                    notifications.includes(NOTIFICATION_KEY_CONSTANTS.CONTENTDEFINITION_CREATE_API_KEY_REQUEST)
                }
                dataAttributes={this.createDataAttributes().Attributes}
                renderAside={() => (
                    <Aside>
                        <AsideItem>
                            <FormActions
                                onSubmit={this.onSubmitForm}
                                permissions={userPermissionsTable.FormActions.definition}
                            />
                        </AsideItem>
                    </Aside>
                )}
            >
                <CreateContentDefinitionForm
                    validateMnemonicId={this.validateMnemonicId}
                    jsonSchema={jsonSchema}
                />
            </ContainerWithAside>
        );
    }
}

export default withFormInitialization(injectIntl(CreateContentDefinition));
